import { useEffect, useMemo, useState } from 'react';
import jobService from 'services/jobService';
import { GalleryDesignSize } from 'utils/galleryDesignSize';
import { useCanvaPublishDraftId } from './useCanvaPublishDraft';

/**
 * Parked-design reads in flight or already resolved, keyed by draft id.
 *
 * Three components on the gallery page ask for the design size independently — the page shell for
 * its remount key, and each of the two views for its own request — and without this each mount
 * would fire the same GET. A draft is immutable for the life of the visit (it is written once and
 * only ever deleted), so sharing the promise is safe and the size is identical everywhere.
 */
const inFlightDraftById = new Map<string, Promise<GalleryDesignSize>>();

const loadDesignSize = (draftId: string): Promise<GalleryDesignSize> => {
  const existing = inFlightDraftById.get(draftId);
  if (existing) {
    return existing;
  }
  const promise = jobService
    .getCanvaPublishDraft(draftId)
    .then((draft) => ({
      ...(draft?.designWidthIn != null
        ? { designWidthIn: draft.designWidthIn }
        : {}),
      ...(draft?.designLengthIn != null
        ? { designLengthIn: draft.designLengthIn }
        : {}),
      ...(draft?.designPageCount != null
        ? { designPageCount: draft.designPageCount }
        : {}),
    }))
    .catch(() => {
      // Dropped from the cache so a later mount can retry; a design that simply could not be
      // measured resolves to {} above and stays cached, because retrying would not help.
      inFlightDraftById.delete(draftId);
      return {} as GalleryDesignSize;
    });
  inFlightDraftById.set(draftId, promise);
  return promise;
};

/**
 * Trim size of the Canva design parked for this gallery visit, for ranking products by how well
 * they fit it.
 *
 * Empty on every normal gallery visit — the request then carries no design size and the API ranks
 * without the dimension tier, which is the pre-existing behaviour.
 *
 * `loading` matters for the caller: the design size changes the order of the whole result, so
 * fetching the list before the size arrives shows one order and then reshuffles it. Callers on a
 * Canva visit should wait.
 *
 * A failed or expired draft read is not surfaced as an error. The buyer can still browse and pick
 * a product — that is what finishes the publish — so losing the ranking is a degradation, not a
 * failure, and an error banner over a working gallery would be worse than the unranked order.
 */
export const useCanvaDesignSize = (): {
  designSize: GalleryDesignSize;
  loading: boolean;
  isCanvaPublish: boolean;
} => {
  const canvaDraftId = useCanvaPublishDraftId();
  const [designSize, setDesignSize] = useState<GalleryDesignSize>({});
  const [loading, setLoading] = useState<boolean>(Boolean(canvaDraftId));

  useEffect(() => {
    if (!canvaDraftId) {
      setDesignSize({});
      setLoading(false);
      return;
    }
    // Guards against a resolved read for a draft the buyer has already navigated away from.
    let current = true;
    setLoading(true);
    loadDesignSize(canvaDraftId)
      .then((size) => {
        if (current) {
          setDesignSize(size);
        }
      })
      .finally(() => {
        if (current) {
          setLoading(false);
        }
      });
    return () => {
      current = false;
    };
  }, [canvaDraftId]);

  return useMemo(
    () => ({ designSize, loading, isCanvaPublish: Boolean(canvaDraftId) }),
    [designSize, loading, canvaDraftId]
  );
};
